/**
 * 山梨県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「枯露柿」は広島の干し柿（西条柿）と題材が重なるので外した。
 * - 「あんびん」は埼玉の「塩あんびん」と名が近いが、山梨のものは手で叩いて平たくして蒸す菓子で、
 *   とうもろこし粉なら黄色、さつまいも粉なら黒くなると公的な出典が書いている。別物として入れた。
 * - 「甲州小梅漬」の「日本一」は、梅ではなく「小梅の生産は日本一を誇っている」という限定つきの
 *   表現なので、そのまま書いている。
 * - 「あけぼの大豆」の「通常の大豆の1.6倍」「10粒並べると6寸」も限定つきのまま書いた。
 * - 「ほうとう」について、公的な出典に書かれているのは「幅1センチ位に切った平らな麺を、
 *   かぼちゃなどの具材とともに味噌仕立ての汁で煮込む」までで、かぼちゃが煮崩れるとは書かれていない。
 * - 「みみ」は麺ではなく、5センチ四方の生地の角を合わせて箕の形にしたもの。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：甲州小梅漬・せいだのたまじ・月の雫・おしゃかこごり・あけぼの大豆の枝豆・うらじろまんじゅう・あんびん・うすやき・めまき（店で食べられる品に差し替え）
 * - 信玄餅：信玄餅が山梨のあべかわもち（お盆の供え物）から生まれたという話は、菓子会社のサイトにはあるが国・県・観光協会の資料で確かめられないので書かない。tip ではあべかわもちの習わしだけを、信玄餅の由来とは結びつけずに書いた
 * - 信玄餅：製造する菓子会社の名前・商品名（金精軒、桔梗屋など）は店の宣伝になるので書かない
 * - 信玄餅：「信玄餅」の商標の持ち主については公式資料で確かめていないので書かない
 * - ぶどう：デラウェアの種なし化技術（昭和34年）を「世界初」とする記述が県ページにあるが、ゲーム側から大きな称号を増やさないため書かない
 * - ぶどう：どの品種が一番多く作られているかは、開いた県ページで確かめていないので書かない
 * - 桃：県ページの「日本一といわれる長い日照時間」は、日照時間の順位の主張になるので「日照時間の長さ」とだけ書いた
 * - 桃：主力品種の名前は品種構成が変わりうるので書かない
 * - 甲州地どりの炭火焼き鳥：甲府市の「甲府ブランド認定農林産物 第1号」（2016年認定）は甲府市産業支援サイトにあるが、字数の都合で今回は入れていない（書いてよい事実）
 * - おざら：甲府市観光協会の『ほうとうの夏版』という言い方は、農水省が『ほうとうより細い麺』と書いているので使わない
 * - 吉田のうどん：「日本一硬い」という言い方は観光サイトの見出しにあるが、順位の主張なので書かない
 * - 吉田のうどん：店の数（40店、約60店など）は資料によって違い、変わる情報なので書かない
 * - 富士の介（山梨のブランドマス）：研究着手の年・出荷開始の年は、県の詳しい資料（PDF）が文字として読めず確かめられなかったので書かない
 * - 富士の介（山梨のブランドマス）：「キングサーモンの血を引くご当地サーモンは富士の介だけ」という唯一性の主張は、開いた県ページで確かめていないので書かない
 * - 鮑の煮貝：製造販売する店の名前と「元祖」「創業400年」などの店の名乗りは書かない
 * - 鮑の煮貝：煮ることでうま味成分が増えるという検証の話は農水省ページにあるが、字数の都合で入れていない（書いてよい事実）
 * - 甲州牛（すき焼き または ステーキ）：甲州ワインビーフ（別ブランド）は混同を避けるため説明しない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { yamanashiSceneryTour } from './yamanashiScenes';
import { YAMANASHI_FOOD_HULLS } from './yamanashiFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'shingen-mochi',
    level: 1,
    name: '信玄餅',
    shortName: '信玄餅',
    image: '/assets/foods/yamanashi/01_shingen_mochi.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '餅にきな粉をまぶし、黒蜜をかけて食べる甘味。武田信玄公が非常食にしていた砂糖入りの餅にちなんで作られたという説もある。きな粉と黒蜜の組み合わせを生かしたアイスやプリンも作られている。',
    source:
      'https://kofu-tourism.com/feature/gourmet/index',
    icon: { label: '信玄餅', shape: 'mochi', baseColor: '#e3c98c', accentColor: '#4a2c1a' },
    reward: {
      unlockScore: 200,
      area: '甲府市ほか県内各地',
      headline: '餅にきな粉と黒蜜をかける',
      tip: '山梨では、もちにきな粉をまぶし黒蜜をかけたものを「あべかわもち」と呼び、お盆に仏様に供えて食べる。お盆の時期には店にきな粉と黒蜜つきの四角いもちが並ぶ。',
      links: searchLinks('甲府市', '信玄餅'),
      official: [{ label: '富士の国やまなし観光ネット「お土産」', url: 'https://www.yamanashi-kankou.jp/taste/omiyage.html' }],
    },
  },
  {
    id: 'yamanashi-budou',
    level: 2,
    name: 'ぶどう',
    shortName: 'ぶどう',
    image: '/assets/foods/yamanashi/02_yamanashi_budou.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '山梨県によると、ぶどうの栽培面積・生産量はともに日本一で、国の記録が残る昭和25年から続いている。日本固有の品種「甲州」は1300年の歴史を持つとされる。出荷は品種によって7月中旬から10月下旬まで。',
    source:
      'https://www.pref.yamanashi.jp/oishii-mirai/nochikusanbutsu/budou.html',
    icon: { label: 'ぶどう', shape: 'berry', baseColor: '#4b2a5c', accentColor: '#6a9a3a' },
    reward: {
      unlockScore: 500,
      area: '山梨県内各地',
      headline: '千年をこえて育てられてきた果物',
      tip: '甲州種の始まりには、約1300年前の718年に僧の行基がぶどう栽培を広めたという説と、約800年前の1186年に雨宮勘解由が見つけたという説がある。',
      links: searchLinks('山梨県', 'ぶどう狩り'),
      official: [{ label: '山梨県 おいしい未来へ やまなし「ぶどう」', url: 'https://www.pref.yamanashi.jp/oishii-mirai/nochikusanbutsu/budou.html' }],
    },
  },
  {
    id: 'yamanashi-momo',
    level: 3,
    name: '桃',
    shortName: '桃',
    image: '/assets/foods/yamanashi/03_yamanashi_momo.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '山梨県によると、ももの生産量は昭和41年に日本一となり、それ以来日本一を続けている。日照時間の長さ、盆地ならではの昼夜の大きな寒暖差、水はけのよい扇状地が栽培を支えている。',
    source:
      'https://www.pref.yamanashi.jp/oishii-mirai/nochikusanbutsu/momo.html',
    icon: { label: 'もも', shape: 'berry', baseColor: '#f2a7a0', accentColor: '#6a9a3a' },
    reward: {
      unlockScore: 1000,
      area: '山梨県内各地',
      headline: '盆地の寒暖差が育てる桃',
      tip: '極早生から極晩生まで品種を替えながら、6月中旬から9月上旬にかけて出荷される。1つの品種を収穫できるのは、おおむね1週間ほど。',
      links: searchLinks('山梨県', '桃狩り'),
      official: [{ label: '山梨県 おいしい未来へ やまなし「もも」', url: 'https://www.pref.yamanashi.jp/oishii-mirai/nochikusanbutsu/momo.html' }],
    },
  },
  {
    id: 'koshu-jidori-yakitori',
    level: 4,
    name: '甲州地どりの炭火焼き鳥',
    shortName: '甲州地どり焼き鳥',
    image: '/assets/foods/yamanashi/04_koshu_jidori_yakitori.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '甲州地どりは、雄の「山梨県産シャモ」と雌の「白色プリマスロック」をかけ合わせて生まれた鶏。120日間ほど広い場所で放し飼いにして育てるため、肉の締まりがよく脂肪が少なく、うま味が凝縮しているという。',
    source:
      'https://www.pref.yamanashi.jp/faq/chikusan/koushuudidori.html',
    icon: { label: '地どり', shape: 'dumpling', baseColor: '#c98a45', accentColor: '#4e2f18' },
    reward: {
      unlockScore: 1800,
      area: '甲府市',
      headline: '放し飼いで育てた地どりを串焼きに',
      tip: '山梨県畜産試験場（いまの山梨県畜産酪農技術センター）と甲州地どり生産組合が開発した。甲府には、甲州地どりにこだわる焼き鳥店や炭火串焼きの店が点在する。',
      links: searchLinks('甲府市', '甲州地どり 焼き鳥'),
      official: [{ label: '山梨県「甲州地どり料理」', url: 'https://www.pref.yamanashi.jp/shokuhin-st/shokuiku/yamanashinoshoku/koshujidori.html' }, { label: '甲府市産業支援サイト「特産第1号 甲州地どり」', url: 'https://www.kofu-sangyo.jp/product/norin/jidori' }],
    },
  },
  {
    id: 'ozara',
    level: 5,
    name: 'おざら',
    shortName: 'おざら',
    image: '/assets/foods/yamanashi/05_ozara.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: 'ゆでて冷水でしめた麺を、温かい醤油ベースのつゆにつけて食べる。つゆには肉や旬の野菜、きのこを入れて具だくさんにすることが多く、小ねぎやみょうがを薬味に添える。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ozara_yama_nashi.html',
    icon: { label: 'おざら', shape: 'noodle-bowl', baseColor: '#efe2c2', accentColor: '#7a5230' },
    reward: {
      unlockScore: 3000,
      area: '甲府市／甲斐市／身延町／昭和町',
      headline: '夏に食べる冷たい麺',
      tip: '盆地の山梨は夏が暑く、冬のほうとうにかわって夏に好まれる。のど越しがよく夏バテ防止に人気で、米が貴重だった時代には暑い時期のごちそうだった。',
      links: searchLinks('甲府市', 'おざら'),
      official: [{ label: '山梨県「おざら」', url: 'https://www.pref.yamanashi.jp/shokuhin-st/shokuiku/yamanashinoshoku/ozara.html' }],
    },
  },
  {
    id: 'yoshida-udon',
    level: 6,
    name: '吉田のうどん',
    shortName: '吉田のうどん',
    image: '/assets/foods/yamanashi/06_yoshida_udon.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '生地を足で踏んでこね、5mmほどの厚さと幅に切ってゆで、冷水でしめる。味噌や醤油の入っただしのきいたつゆで食べ、蒸したキャベツや、砂糖と醤油で煮た馬肉をのせる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/yoshida_no_udon_yama_nashi.html',
    icon: { label: 'うどん', shape: 'noodle-bowl', baseColor: '#efe6cf', accentColor: '#7a3b2a' },
    reward: {
      unlockScore: 4500,
      area: '富士吉田市',
      headline: 'キャベツと馬肉をのせる太いうどん',
      tip: '昭和初期、織物業が盛んだった富士吉田では、機械を操る女性たちの昼食づくりで仕事が止まらないよう、行商の男性たちがうどんを作るようになったとされる。',
      links: searchLinks('富士吉田市', '吉田のうどん'),
      official: [{ label: '富士の国やまなし観光ネット「富士吉田市のソウルフード『吉田のうどん』」', url: 'https://www.yamanashi-kankou.jp/special/yoshidanoudon_osusume.html' }],
    },
  },
  {
    id: 'tori-motsuni',
    level: 7,
    name: '鳥もつ煮',
    shortName: '鳥もつ煮',
    image: '/assets/foods/yamanashi/07_tori_motsuni.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '強火で短時間で照り煮にし、鳥もつの旨味や甘みをぎゅっと閉じ込める。具はレバー、砂肝、はつ、きんかん、ひも。フライパンのふちにたれがこびりつく状態で完成になる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/torino_motsuni_yama_nashi.html',
    icon: { label: '鳥もつ煮', shape: 'curry-plate', baseColor: '#3a2015', accentColor: '#e0b02a' },
    reward: {
      unlockScore: 6500,
      area: '甲府市',
      headline: '捨てられていたもつから',
      tip: '戦後の昭和25年ごろ、甲府市の蕎麦屋の二代目主人が肉屋から相談を受けて開発したとされる。食糧不足の時代に捨てられていた鳥のもつを活かし、醤油と砂糖でこってりと煮た料理として完成した。',
      links: searchLinks('甲府市', '鳥もつ煮'),
    },
  },
  {
    id: 'mimi',
    level: 8,
    name: 'みみ',
    shortName: 'みみ',
    image: '/assets/foods/yamanashi/08_mimi.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '小麦粉をこねてのばし、5センチ四方に切った生地の片側の二つの角に水をつけて合わせ、三角にする。農機具の「箕（み）」の形に似ていることから名がついた。だし汁で野菜と煮込み味噌で調える。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/mimi_yama_nashi.html',
    icon: { label: 'みみ', shape: 'rice-bowl', baseColor: '#f0ead8', accentColor: '#c2a06a' },
    reward: {
      unlockScore: 9000,
      area: '富士川町十谷',
      headline: '福をすくう「福箕」',
      tip: '「福をすくう」意味をこめて「福箕」といわれ、それが転じて「みみ」になったともいわれる。十谷では毎年、正月元旦の朝食にみみを歳神様に供えてから家族で食べる。',
      links: searchLinks('富士川町', 'みみ'),
    },
  },
  {
    id: 'fujinosuke',
    level: 9,
    name: '富士の介（山梨のブランドマス）',
    shortName: '富士の介',
    image: '/assets/foods/yamanashi/09_fujinosuke.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'キングサーモンとニジマスをかけ合わせた、山梨県オリジナルのブランド魚。鮮やかなサーモンピンクの身はきめが細かく、ほどよく上品な脂がのる。うま味成分はニジマスの約1.6倍という。',
    source:
      'https://www.pref.yamanashi.jp/oishii-mirai/contents/onlyone/fujinosuke.html',
    icon: { label: '富士の介', shape: 'sashimi-platter', baseColor: '#f08a5d', accentColor: '#f6e7d8' },
    reward: {
      unlockScore: 12000,
      area: '山梨県内各地',
      headline: 'キングサーモンの血を引くマス',
      tip: '淡水では餌を食べず育てにくいキングサーモンと、育てやすいニジマスを合わせた魚。山梨県水産技術センター忍野支所が10年以上の研究を重ねて生み出した。',
      links: searchLinks('山梨県', '富士の介 刺身'),
      official: [{ label: '山梨県 おいしい未来へ やまなし「富士の介について」', url: 'https://www.pref.yamanashi.jp/oishii-mirai/contents/onlyone/fujinosuke.html' }],
    },
  },
  {
    id: 'houtou',
    level: 10,
    name: 'ほうとう',
    shortName: 'ほうとう',
    image: '/assets/foods/yamanashi/10_houtou.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '小麦粉を練り、打ち粉をしながら薄くのばして幅1センチ位に切った「ほうとうめん」を、たっぷりの具材とともに味噌仕立ての汁で煮込む。生地に塩を混ぜないので、加熱のときに塩抜きが要らない。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/houtou_yama_nashi.html',
    icon: { label: 'ほうとう', shape: 'pot', baseColor: '#e0c07a', accentColor: '#c9702a' },
    reward: {
      unlockScore: 16000,
      area: '県内全域',
      headline: '幅1センチの平たい麺',
      tip: '峡南地域では「のしいれ」「のしこみ」とも呼ばれる。2007年に農林水産省が選んだ「農山漁村の郷土料理百選」に含まれている。',
      links: searchLinks('甲府市', 'ほうとう'),
    },
  },
  {
    id: 'awabi-nigai',
    level: 11,
    name: '鮑の煮貝',
    shortName: '鮑の煮貝',
    image: '/assets/foods/yamanashi/11_awabi_nigai.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '殻つきのあわびをよく洗い、蒸すなどして火を通してから殻から外し、醤油ベースのたれにじっくり漬けこんで煮る。薄く切って、しそやきゅうりと盛りつけて食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/traditional-foods/menu/awabino_nigai.html',
    icon: { label: '煮貝', shape: 'sashimi-platter', baseColor: '#7a4a22', accentColor: '#9fb7b0' },
    reward: {
      unlockScore: 22000,
      area: '甲府市',
      headline: '海のない甲州に運ばれたあわび',
      tip: '約400年前の江戸時代に生まれたとされる。駿河でとれたあわびを醤油漬けにして樽に詰め、馬で峠を越えて運ぶうちに、揺れと馬の体温でちょうどよい味になったという説がよく知られる。',
      links: searchLinks('甲府市', 'あわびの煮貝'),
      official: [{ label: '富士の国やまなし観光ネット「お土産」', url: 'https://www.yamanashi-kankou.jp/taste/omiyage.html' }],
    },
  },
  {
    id: 'koshu-gyu',
    level: 12,
    name: '甲州牛（すき焼き または ステーキ）',
    shortName: '甲州牛',
    image: '/assets/foods/yamanashi/12_koshu_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '山梨の自然の中で、甲州牛・甲州ワインビーフ推進協議会の会員が育てた黒毛和種の肥育牛のうち、肉質が4・5等級に格付けされたものだけが「甲州牛」として売られる。やわらかい肉質と鮮やかな肉色が特徴。',
    source:
      'https://www.pref.yamanashi.jp/faq/chikusan/koushuugyuu.html',
    icon: { label: '甲州牛', shape: 'hotpot', baseColor: '#9c3b2e', accentColor: '#f1d9c8' },
    reward: {
      unlockScore: 30000,
      area: '山梨県内各地',
      headline: '等級で選ばれる山梨の黒毛和牛',
      tip: '甲府では、焼き肉や鉄板焼き、フレンチなど、さまざまな飲食店で甲州牛を味わうことができる。',
      links: searchLinks('甲府市', '甲州牛 すき焼き'),
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const yamanashiFoodStage: StageDefinition = {
  regionId: 'yamanashi',
  regionName: '山梨県',
  mode: 'food',
  title: '山梨めしマージ',
  theme: {
    primaryColor: '#6b2f4a',
    secondaryColor: '#efe6d6',
    accentColor: '#3f6b8a',
    backgroundColor: '#faf5ec',
  },
  /** 背景ツアー「山梨めぐり」。軸は季節。4月の山高神代ザクラから始めて、3月の仙娥滝で終わる。富士のまわりと甲府盆地を行き来する。 */
  scenery: {
    tour: yamanashiSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, YAMANASHI_FOOD_HULLS),
};
